import type { Building } from "building-types/building/buildingTypes";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { RWTable, Table } from "common/data/table";
import type { DocOptionStateView } from "./docOptionStateView";
import { optionSetDefaultOptionId } from "./optionSetBaseUtils";
import { exteriorPaletteOptionSetId } from "./settingsOptionSet";

export const DEFAULT_INTERIOR_PALETTE_NAME = "Interior Palette";
export const DEFAULT_EXTERIOR_PALETTE_NAME = "Exterior Palette";
export const PALETTE_OPTION_SET_SUFFIX = "Palette]";

// TODO: Update "SettingsParam" to "SettingsOptionSet"
export enum PaletteTags {
  EXTERIOR_PALETTE = "ExteriorPalette",
  INTERIOR_PALETTE = "InteriorPalette",
  SETTINGS_OPTION_SET = "SettingsParam",
  PALETTES = "palettes",
}

export function isExteriorPaletteOptionSet(optionSet: Pick<DocOptionSet, "tags"> | undefined) {
  return optionSet?.tags?.includes(PaletteTags.EXTERIOR_PALETTE);
}

export const DEFAULT_INTERIOR_PALETTE_TAGS = [
  PaletteTags.INTERIOR_PALETTE,
  PaletteTags.SETTINGS_OPTION_SET,
  PaletteTags.PALETTES,
];

export const DEFAULT_EXTERIOR_PALETTE_TAGS = [
  PaletteTags.EXTERIOR_PALETTE,
  PaletteTags.SETTINGS_OPTION_SET,
  PaletteTags.PALETTES,
];

export function isInteriorPaletteOptionSet(optionSet: Pick<DocOptionSet, "tags">) {
  return optionSet?.tags?.includes(PaletteTags.INTERIOR_PALETTE);
}

export function isPaletteOptionSet(optionSet: DocOptionSet) {
  return isExteriorPaletteOptionSet(optionSet) || isInteriorPaletteOptionSet(optionSet);
}

export function exteriorPaletteOptionSetIdFromBuilding(building: DocOptionStateView) {
  const settingsId = building.settings.exteriorMaterials.id;

  return exteriorPaletteOptionSetId(settingsId ?? "");
}

export function baseExteriorPaletteOptionFromBuilding(building: Building): DocOption | undefined {
  const optionSetId = exteriorPaletteOptionSetIdFromBuilding(building);
  const optionSet = building.evaluatedOptionSets[optionSetId];
  const defaultValue = optionSetDefaultOptionId(optionSet, building.evaluatedOptionSets);

  return defaultValue ? optionSet?.options?.[defaultValue] : undefined;
}

/**
 * Removes orphaned interior-palette option sets.
 * Copy-on-write: returns the input table when nothing is removed; otherwise a new table.
 * Callers must use the returned value (do not rely on in-place mutation).
 */
export function removeOrphanedInteriorPaletteOptionSet(
  optionSets: Table<DocOptionSet> | undefined | null,
): Table<DocOptionSet> {
  if (!optionSets) {
    return {};
  }

  let optionIds: string[] = [];

  for (const optionSet of Object.values(optionSets)) {
    if (DEFAULT_INTERIOR_PALETTE_TAGS.every((tag) => optionSet?.tags?.includes(tag))) {
      optionIds = Object.keys(optionSet.options ?? {});
      break;
    }
  }

  const idsToRemove: string[] = [];
  for (const [optionSetId, optionSet] of Object.entries(optionSets)) {
    if (optionSet?.tags?.length === 1 && isInteriorPaletteOptionSet(optionSet)) {
      if (Object.keys(optionSet.options ?? {}).every((x) => optionIds.findIndex((y) => x === y) !== -1)) {
        idsToRemove.push(optionSetId);
      }
    }
  }

  if (idsToRemove.length === 0) {
    return optionSets;
  }

  const next: RWTable<DocOptionSet> = { ...optionSets };
  for (const optionSetId of idsToRemove) {
    delete next[optionSetId];
  }
  return next;
}
